import { useCallback, useEffect } from 'react';

import { PDFCustomDataKey } from '@aline/common';
import { useMaybeDocumentViewer } from 'src/components/viewer/viewer-context';
import { useEditorFindStateSelector } from 'src/editor/editor-find-state-registry';

export function useRegisterSearchListeners() {
  const documentViewer = useMaybeDocumentViewer();
  const search = useEditorFindStateSelector(state => state.search);
  const scrollViewEl = documentViewer?.getScrollViewElement() ?? null;
  const { clearSearchHighlights } = useClearSearchHighlights();

  useEffect(() => {
    if (!search) scrollViewEl?.addEventListener('click', clearSearchHighlights);

    return () => {
      scrollViewEl?.removeEventListener('click', clearSearchHighlights);
    };
  }, [clearSearchHighlights, scrollViewEl, search]);
}

export function useClearSearchHighlights() {
  const documentViewer = useMaybeDocumentViewer();
  const clearSearchHighlights = useCallback(async () => {
    if (!documentViewer) return;

    const annotationManager = documentViewer.getAnnotationManager();
    const searchAnnotations = annotationManager.getAnnotationsList().filter(annotation => {
      const value = annotation.getCustomData(PDFCustomDataKey.searchHighlight);
      return value === String(true);
    });
    const pageNumsToRedraw = new Set(searchAnnotations.map(annotation => annotation.PageNumber));

    documentViewer.clearSearchResults();
    annotationManager.deleteAnnotations(searchAnnotations);
    await Promise.all(
      pageNumsToRedraw
        .values()
        .map(pageNumber => annotationManager.drawAnnotations({ majorRedraw: true, pageNumber }))
        .toArray()
    );
  }, [documentViewer]);

  return { clearSearchHighlights };
}
